import { useEffect, useReducer, useRef, useState } from "react";
import { ConfirmationModal } from "./ConfirmationModal.tsx";
import { usePendingUpdate } from "./pendingUpdate.ts";
import { takePendingUpdate } from "./checkForAppUpdate.ts";
import {
  UPDATE_PROMPT_COPY,
  initialUpdatePromptState,
  isAnotherDialogOpen,
  isUpdatePromptVisible,
  readUpdatePromptDismissed,
  reduceUpdatePrompt,
  rememberUpdatePromptDismissed,
} from "./updatePrompt.ts";

/** How often to re-check whether the other dialog has closed. */
const DIALOG_RECHECK_MS = 1000;
/**
 * The prompt can appear under a finger that was already tapping the page:
 * an Update now that soon was not aimed at it (it would reload mid-entry).
 */
const UPDATE_TAP_GUARD_MS = 500;

/**
 * "Update Available!" — Update now / Later (in-game parity, client
 * 2026-10-07).
 *
 * Mounted once, in the dashboard shell, so it shows in every practice app the
 * moment a held update lands (or is found waiting at boot). The red dots on
 * the sigma and on Check For Updates stay until the update is taken — from
 * here or from the drawer.
 */
export const UpdateAvailableModal = () => {
  const pending = usePendingUpdate();
  const [state, dispatch] = useReducer(
    reduceUpdatePrompt,
    readUpdatePromptDismissed(),
    initialUpdatePromptState
  );
  const [updating, setUpdating] = useState(false);
  const [failed, setFailed] = useState(false);
  // Starts BLOCKED: the check for another dialog only runs while our own
  // modal is not mounted, so it can never see itself.
  const [blocked, setBlocked] = useState(true);

  useEffect(() => {
    dispatch({
      type: "pending",
      pending,
      dismissed: pending ? readUpdatePromptDismissed() : undefined,
    });
  }, [pending]);

  const wanted = isUpdatePromptVisible(state);
  const shownAt = useRef(0);

  // Hold while another dialog is open; once it is clear, show and stop
  // checking until the next update.
  useEffect(() => {
    if (!wanted) {
      setBlocked(true);
      return;
    }
    if (!isAnotherDialogOpen()) {
      shownAt.current = Date.now();
      setBlocked(false);
      return;
    }
    const timer = setInterval(() => {
      if (isAnotherDialogOpen()) return;
      clearInterval(timer);
      shownAt.current = Date.now();
      setBlocked(false);
    }, DIALOG_RECHECK_MS);
    return () => clearInterval(timer);
  }, [wanted]);

  if (!wanted || blocked) return null;

  const later = () => {
    if (updating) return;
    rememberUpdatePromptDismissed();
    dispatch({ type: "later" });
  };

  const update = async () => {
    if (Date.now() - shownAt.current < UPDATE_TAP_GUARD_MS) return;
    setUpdating(true);
    setFailed(false);
    const taken = await takePendingUpdate();
    // Taken → the page is reloading onto the new build. Not taken → the
    // worker never took over (or vanished): say so, and keep the prompt
    // open — Check For Updates can still probe for it.
    if (!taken) {
      setUpdating(false);
      setFailed(true);
    }
  };

  return (
    <ConfirmationModal
      visible
      title={UPDATE_PROMPT_COPY.title}
      onClose={later}
      onConfirm={() => void update()}
      cancelText={UPDATE_PROMPT_COPY.dismiss}
      confirmText={UPDATE_PROMPT_COPY.confirm}
      loadingText={UPDATE_PROMPT_COPY.updating}
      isLoading={updating}
    >
      <p>
        {UPDATE_PROMPT_COPY.bodyBefore}
        <strong>{UPDATE_PROMPT_COPY.bodyBrand}</strong>
        {UPDATE_PROMPT_COPY.bodyAfter}
      </p>
      {failed && <p className="text-red-600">{UPDATE_PROMPT_COPY.failed}</p>}
    </ConfirmationModal>
  );
};
